import { CustomerBillerStoreService } from '../../../billers/services/customer-biller-store.service';
import { LiabilitySource } from '../../../billers/models/model';
import { HandsetService } from '../../../core/services/handset.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfig } from 'src/app/core/services/app-config.service';
import { ToolbarService } from '../../../core/services/toolbar.service';
import { AddLiabilityScanForm } from '../../forms/add-liability-scan.form';
import { switchMap, tap } from 'rxjs/operators';
import { BillerSearchService } from 'src/app/billers/services/biller-search.service';
import { DialogService } from 'src/app/core/services/dialog.service';
import { AddBillerDialogComponent } from '../../components/add-biller-dialog/add-biller-dialog.component';
import { MAX_WIDTH_90, WIDTH_360 } from 'src/app/shared/configs/dialog-config';
import { LimitedCustomerBiller } from '../../models/limited-biller.model';
import { LiabilityService } from '../../services/liability.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { BillerService } from '../../../billers/services/biller.service';
import { LoaderButtonType } from '../../../shared/models/loader-button-type.enum';

@Component({
standalone: false,
  selector: 'app-liability-data-scan-confirmation',
  templateUrl: './liability-data-scan-confirmation.component.html',
  styleUrls: ['./liability-data-scan-confirmation.component.scss'],
})
export class LiabilityDataScanConfirmationComponent implements OnInit {
  @Input()
  public form: AddLiabilityScanForm;

  @Input()
  public liabilityAutomaticReadingId: string;
  public saving = false;

  public customerBillers: LimitedCustomerBiller[];
  public urlSafe: SafeResourceUrl;
  public url: SafeResourceUrl;
  public documentPreviewVisible = false;

  private foundBiller: string;
  public readingError: boolean;
  public readonly MIN_AMOUNT = 0.01;

  @Output()
  modifyClickEvent: EventEmitter<any> = new EventEmitter<{ title: string, subtitle: string }>();

  private modificationTitle = 'Zmień dane';
  private modificationSubtitle = 'Uzupełnij dane i sprawdź ich poprawność.';

  private errorTitle = 'Uzupełnij i zweryfikuj';
  private errorSubtitle = 'Nie udało nam się odczytać wszystkich danych z przesłanego dokumentu. ' +
    'Uzupełnij i sprawdź poprawność  informacji.';

  private validationErrorTitle = 'Popraw dane';
  private validationErrorSubtitle = 'Sprawdź poprawność danych.';


  constructor(
    public appConfig: AppConfig,
    public handsetService: HandsetService,
    public billerSearch: BillerSearchService,
    private activatedRoute: ActivatedRoute,
    private toolbar: ToolbarService,
    private router: Router,
    private customerBillerStoreService: CustomerBillerStoreService,
    private dialog: DialogService,
    private liabilityService: LiabilityService,
    private sanitizer: DomSanitizer,
    private billerService: BillerService,
  ) {
    this.toolbar.init(this.activatedRoute);
  }

  ngOnInit(): void {
    if (this.form.billerId.value === null || this.form.bban.value === null || this.form.amount === null) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      this.modifyClickEvent.next({
        title: this.errorTitle,
        subtitle: this.errorSubtitle,
      });
    } else if (!this.form.hasAmountInLimit()) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      this.modifyClickEvent.next({
        title: this.validationErrorTitle,
        subtitle: this.validationErrorSubtitle,
      });
    }

  }

  public addBiller(): void {
    this.dialog
      .open(AddBillerDialogComponent, {
        maxWidth: MAX_WIDTH_90,
        width: WIDTH_360,
        data: this.foundBiller,
      })
      .afterClosed()
      .pipe(
        tap(({ billerId, customerBillerId, displayName }) => {
          if (customerBillerId) {
            this.pushAndSetValue({ billerId, customerBillerId, displayName });
          } else {
            this.customerBillers = this.customerBillers.filter((biller) => biller.customerBillerId !== '0');
            this.pushAndSetValue({ billerId, customerBillerId: '0', displayName });
          }
        }),
      )
      .subscribe();
  }

  public submit = () => {
    this.saving = true;
    if (this.form.invalid) {
      this.saving = false;
      return of();
    }
    return this.liabilityService
      .addLiabilityFromAutomaticReading(this.liabilityAutomaticReadingId, this.form.getFinalData())
      .pipe(
        tap(() => {
          this.saving = false;
        }),
        switchMap((data) => this.navigateToLiabilities('scan-completed', data)),
      );
  };


  private navigateToLiabilities(...args: string[]): Promise<boolean> {
    return this.router.navigate([`liabilities/${args.join('/')}`]);
  }

  private addBillerOnSubmit(biller: string): Observable<string> {
    return this.billerService.addBiller(biller).pipe(
      switchMap((result: string) =>
        this.addCustomerBillerOnSubmit({
          billerId: result,
          displayName: result,
          customerBillerId: null,
        }),
      ),
    );
  }

  private addCustomerBillerOnSubmit(biller: LimitedCustomerBiller): Observable<any> {
    return this.customerBillerStoreService.addCustomerBiller(biller.billerId, LiabilitySource.MANUAL).pipe(
      switchMap((customerBillerId: string) => {
        this.overrideCustomerBillerId(customerBillerId);
        this.form.customerBillerId.setValue(customerBillerId);
        return this.customerBillerStoreService.updateLiabilitySource(customerBillerId, LiabilitySource.MANUAL);
      }),
      switchMap(() =>
        this.liabilityService.addLiabilityFromAutomaticReading(
          this.liabilityAutomaticReadingId,
          this.form.getFinalData(),
        ),
      ),
      tap((data) => this.navigateToLiabilities('scan-completed', data)),
    );
  }

  private pushAndSetValue({ billerId, customerBillerId, displayName }) {
    this.customerBillers.push({
      billerId,
      customerBillerId,
      displayName,
    });
    this.form.customerBillerId.setValue(customerBillerId);
  }

  private overrideCustomerBillerId(newCustomerBillerId: string) {
    const biller = this.customerBillers.find((biller) => biller.customerBillerId === '0');
    if (biller) {
      biller.customerBillerId = newCustomerBillerId;
    }
  }

  onModifyClick() {
    this.modifyClickEvent.next({
      title: this.modificationTitle,
      subtitle: this.modificationSubtitle,
    });
  }

  protected readonly LoaderButtonType = LoaderButtonType;
}
